// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CircularProgress, Paper } from '@mui/material'
import classNames from 'classnames'
import React from 'react'
import { Query } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import { removeTypename } from '../../common/utils'
import dashboardCss from '../OldDashboard/oldDashboard.css'
import { i18n } from '../index'
import { fetchChainScoreCardStats, fetchStationScoreCardStats } from './ScoreCardQueries'
import css from './scoreCard.css'

const sections = ['sales', 'station', 'wastage', 'market', 'marketWastage']

type Sections = 'sales' | 'station' | 'wastage'
type ScoreCardStats = {
  actual: number
  high: number
  low: number
  section: Sections
}
type TimePeriods = {
  month: ScoreCardStats
  week: ScoreCardStats
  lastYearToDate: ScoreCardStats
  thisYearToDate: ScoreCardStats
  day: ScoreCardStats
}
type Row = [string, TimePeriods]
const totalThresholds = {
  high: 13,
  low: 5,
}

const getColorClassName = ([category, values]: Row, type: string): string | null | undefined => {
  const { actual, high, low } = values[type]
  const lowerIsBetterFields = [
    'buffetWastage',
    'alacarteWastage',
    'sweetWastage',
    'saltyWastage',
    'pizzatableWastage',
    'abcpizzaWastage',
    'abcburgerWastage',
    'fastfoodWastage',
    'pikacarteWastage',
    'arnoldsWastage',
    'breakfastWastage',
    'buffetWorldWastage',
    'freshBakeryWastage',
    'readyMealsWastage',
    'fruitsAndVegetablesWastage',
    'breweryDrinkWastage',
    'flowersWastage',
    'meatWastage',
    'dryGroceriesWastage',
    'juicesWastage',
    'cheesesWastage',
    'liquidDairyWastage',
    'dairySnacksWastage',
    'tobaccoWastage',
    'frozenProductsWastage',
  ]
  const lowerIsBetter = lowerIsBetterFields.includes(category)
  let className

  if (!actual && actual !== 0) {
    return null
  }

  // total efficiency is only compared to its high threshold, which equals targetEfficiency
  if (category === 'totalBudgetEfficiency') {
    if (actual >= high) {
      className = css.colorCircleGreen
    } else {
      className = css.colorCircleRed
    }
  } else {
    if (lowerIsBetter) {
      className = css.colorCircleGreen

      if (actual <= low) {
        className = css.colorCircleYellow
      } else if (actual >= high) {
        className = css.colorCircleRed
      }
    } else {
      className = css.colorCircleYellow

      if (actual >= high) {
        className = css.colorCircleGreen
      } else if (actual < low) {
        className = css.colorCircleRed
      }
    }
  }

  return className
}

const getSectionColorClassName = (sectionRows: Row[], section: Sections): string => {
  let highValues = 0
  let className = css.borderYellow
  let sectionThresholds
  sectionRows.forEach((row) => {
    const values = row[1]
    sectionThresholds = values.sectionThresholds || {
      high: 0,
      low: 0,
    }
    const rowClassName = getColorClassName(row, 'lastMonth')

    if (rowClassName === css.colorCircleGreen) {
      highValues = highValues + 1
    }
  })
  if (!sectionThresholds) {
    return className
  }

  if (highValues >= sectionThresholds.high) {
    className = css.borderGreen
  } else if (highValues <= sectionThresholds.low) {
    className = css.borderRed
  }

  return className
}

const calculateGrandTotals = (data: {
  scoreCardStats: Record<string, TimePeriods>
}): {
  positiveValues: number
  totalValues: number
} => {
  const rows = Object.entries(removeTypename(data.scoreCardStats))
  const start = {
    positiveValues: 0,
    totalValues: 0,
  }
  return rows.reduce((prev, current) => {
    const [, values] = current

    if (values) {
      const className = getColorClassName(current, 'lastMonth')

      if (className === css.colorCircleGreen || (values.section === 'wastage' && className === css.colorCircleYellow)) {
        return {
          positiveValues: prev.positiveValues + 1,
          totalValues: prev.totalValues + 1,
        }
      }

      return {
        positiveValues: prev.positiveValues,
        totalValues: prev.totalValues + 1,
      }
    } else {
      return prev
    }
  }, start)
}

class ScoreCard extends React.Component {
  render() {
    const {
      match: { params },
    } = this.props
    const sectionCategoryOrder = {
      sales: ['incentiveSales', 'totalSalesEfficiency'],
      station: [
        'thumbsUp',
        'feelingMeterAverage',
        'dailyChecklistUsage',
        'dailyInfoUsage',
        'totalBudgetEfficiency',
        'budgetEfficiency',
        'feelingMeterUsage',
        'targetEfficiency',
        'toiletChecks',
        'hourlyHallChecklistUsage',
        'hourlyMarketChecklistUsage',
      ],
      wastage: [
        'buffetWastage',
        'alacarteWastage',
        'sweetWastage',
        'saltyWastage',
        'pizzatableWastage',
        'abcpizzaWastage',
        'abcburgerWastage',
        'fastfoodWastage',
        'pikacarteWastage',
        'arnoldsWastage',
        'breakfastWastage',
        'buffetWorldWastage',
      ],
      market: ['impulseSales'],
      marketWastage: [
        'freshBakeryWastage',
        'readyMealsWastage',
        'fruitsAndVegetablesWastage',
        'breweryDrinkWastage',
        'flowersWastage',
        'meatWastage',
        'dryGroceriesWastage',
        'juicesWastage',
        'cheesesWastage',
        'liquidDairyWastage',
        'dairySnacksWastage',
        'tobaccoWastage',
        'frozenProductsWastage',
      ],
    }
    const queryVariables = params.stationId
      ? {
          stationId: params.stationId,
        }
      : undefined
    return (
      <div className={css.component}>
        <Query
          query={queryVariables ? fetchStationScoreCardStats : fetchChainScoreCardStats}
          variables={queryVariables}
        >
          {({ loading, error, data }) => {
            if (loading || error) {
              return <CircularProgress />
            }

            const grandTotals = calculateGrandTotals(data)
            return (
              <Paper>
                <div className={classNames(css.component, dashboardCss.widget, css.container)}>
                  <h3>{i18n.t('scoreCard.title')}</h3>
                  <div className={dashboardCss.widgetContent}>
                    <table className={css.table}>
                      <thead>
                        <tr>
                          <th />
                          <th>{i18n.t('scoreCard.columnHeaderThresholds')}</th>
                          <th>{i18n.t('scoreCard.columnHeaderLastYearToDate')}</th>
                          <th>{i18n.t('scoreCard.columnHeaderThisYearToDate')}</th>
                          <th>{i18n.t('scoreCard.columnHeaderLastMonth')}</th>
                          <th>{i18n.t('scoreCard.columnHeaderLastWeek')}</th>
                          <th>{i18n.t('scoreCard.columnHeaderYesterday')}</th>
                        </tr>
                      </thead>

                      {sections.map((section) => {
                        const categoryOrder = sectionCategoryOrder[section]
                        const orderMax = categoryOrder.length
                        const sectionCategories = Object.entries(removeTypename(data.scoreCardStats))
                          .filter(([, val]) => val && val.section === section)
                          .sort((a, b) => {
                            const aValue = categoryOrder.indexOf(a[0]) >= 0 ? categoryOrder.indexOf(a[0]) : orderMax + 1
                            const bValue = categoryOrder.indexOf(b[0]) >= 0 ? categoryOrder.indexOf(b[0]) : orderMax + 1
                            if (aValue < bValue) return -1
                            if (aValue > bValue) return 1
                            return 0
                          })
                        return (
                          <tbody
                            key={section}
                            className={classNames(css.section, getSectionColorClassName(sectionCategories, section))}
                          >
                            <tr>
                              <th className={css.sectionHeader}>{i18n.t(`scoreCard.sectionHeader.${section}`)}</th>
                            </tr>

                            {sectionCategories.map((row: any, i: number) => {
                              const [category, values] = row
                              const { lastYearToDate, thisYearToDate, lastMonth, lastWeek, yesterday } = values || {}
                              const { low, high } = lastMonth || {
                                low: 0,
                                high: 0,
                              }
                              return (
                                <tr key={i}>
                                  <td className={css.name}>{i18n.t(`scoreCard.${category}`)}</td>

                                  <td>
                                    <div className={css.cellValue}>
                                      <span className={css.boundaryValue}>
                                        {category === 'totalBudgetEfficiency' ? `>=${high}` : `${low} - ${high}`}
                                      </span>
                                    </div>
                                  </td>

                                  <td>
                                    <div className={css.cellValue}>
                                      <span
                                        className={classNames(
                                          css.colorCircle,
                                          getColorClassName(row, 'lastYearToDate'),
                                        )}
                                      />
                                      {lastYearToDate.actual}
                                    </div>
                                  </td>
                                  <td>
                                    <div className={css.cellValue}>
                                      <span
                                        className={classNames(
                                          css.colorCircle,
                                          getColorClassName(row, 'thisYearToDate'),
                                        )}
                                      />
                                      {thisYearToDate.actual}
                                    </div>
                                  </td>
                                  <td className={css.month}>
                                    <div className={css.cellValue}>
                                      <span
                                        className={classNames(css.colorCircle, getColorClassName(row, 'lastMonth'))}
                                      />
                                      {lastMonth.actual}
                                    </div>
                                  </td>
                                  <td className={css.week}>
                                    <div className={css.cellValue}>
                                      <span
                                        className={classNames(css.colorCircle, getColorClassName(row, 'lastWeek'))}
                                      />
                                      {lastWeek.actual}
                                    </div>
                                  </td>
                                  <td className={css.day}>
                                    {values.yesterday && (
                                      <div className={css.cellValue}>
                                        <span
                                          className={classNames(css.colorCircle, getColorClassName(row, 'yesterday'))}
                                        />
                                        {yesterday.actual}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        )
                      })}
                      <tbody
                        className={classNames(css.section, {
                          [css.borderRed]: grandTotals.positiveValues <= totalThresholds.low,
                          [css.borderYellow]:
                            grandTotals.positiveValues > totalThresholds.low &&
                            grandTotals.positiveValues < totalThresholds.high,
                          [css.borderGreen]: grandTotals.positiveValues >= totalThresholds.high,
                        })}
                      >
                        <tr>
                          <td className={css.totalsHeading}>{i18n.t('scoreCard.grandTotal')}</td>
                          <td className={css.boundaryValue}>{`${totalThresholds.low} - ${totalThresholds.high}`}</td>
                          <td></td>
                          <td></td>
                          <td className={css.totalsValue}>
                            <div className={css.cellValue}>
                              <span
                                className={classNames(css.colorCircle, {
                                  [css.colorCircleRed]: grandTotals.positiveValues <= totalThresholds.low,
                                  [css.colorCircleYellow]:
                                    grandTotals.positiveValues > totalThresholds.low &&
                                    grandTotals.positiveValues < totalThresholds.high,
                                  [css.colorCircleGreen]: grandTotals.positiveValues >= totalThresholds.high,
                                })}
                              />
                              {`${grandTotals.positiveValues} / ${grandTotals.totalValues}`}
                            </div>
                          </td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Paper>
            )
          }}
        </Query>
      </div>
    )
  }
}

export default withRouter(ScoreCard)
